import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, AlertTriangle, CheckCircle2, ShieldCheck } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import FileUpload from "@/components/FileUpload";
import { toast } from "sonner";

const HELPS = [
  "Understanding what has happened",
  "Reviewing photos, reports and contractor information",
  "Considering whether damage may be insurance-related",
  "Helping prevent uninsured losses",
  "Supporting better evidence and repair decisions",
  "Providing reports where appropriate",
];

export default function Insurance() {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    park_name: "", plot_number: "",
    active_claim: false,
    insurer_name: "", claim_number: "",
    incident_date: "", cause_summary: "",
    help_needed: "",
  });
  const [uploadIds, setUploadIds] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k,v) => setForm(f=>({...f,[k]:v}));

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/support/insurance", { ...form, upload_ids: uploadIds });
      setDone(true);
      toast.success("Insurance enquiry received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  }

  if (done) return <Confirmation/>;

  return (
    <div data-testid="insurance-page">
      <SEO
        title="Park Home Insurance Guidance | Avoid Uninsured Losses"
        description="Non-regulated technical guidance on park home damage, repair scope and claim-related considerations. Independent advice before you submit or approve anything."
        keywords="park home insurance guidance, park home insurance claim advice, park home uninsured losses, park home damage claim UK"
        canonical="https://parkhomesupport.co.uk/insurance"
      />
      <section className="hero-gradient section-pad">
        <div className="container-phs">
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-7">
              <span className="eyebrow">Insurance Guidance</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3 max-w-3xl text-[#071A33] leading-[1.05]">
                Avoid Uninsured Losses and <span className="text-[#064E2E]">Poor Claims Decisions.</span>
              </h1>
              <p className="mt-6 max-w-2xl text-lg text-[#334155]">
                Park home claims can fail when the cause, scope or evidence is misunderstood. We provide non-regulated technical guidance to help you understand the issue before decisions are made.
              </p>
              <div className="mt-8 card-default border-l-4 border-[#B88A2A] bg-[#FFF8EC]" data-testid="ins-disclaimer">
                <div className="flex gap-3 items-start">
                  <AlertTriangle className="text-[#B88A2A] mt-0.5 shrink-0" size={20}/>
                  <p className="text-sm text-[#3F2E10] leading-relaxed">
                    <strong>Important:</strong> We do not sell, arrange or manage insurance. We provide practical, technical and document-based guidance to help you understand damage, repair scope and possible claim-related considerations.
                  </p>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="card-default" data-testid="ins-helps-card">
                <span className="eyebrow">What We Help With</span>
                <h3 className="font-display text-2xl mt-2 text-[#071A33]">Where guidance pays for itself</h3>
                <ul className="mt-5 space-y-2.5 text-[#334155]">
                  {HELPS.map((h) => (
                    <li key={h} className="flex gap-2.5"><CheckCircle2 size={18} className="text-[#0F766E] mt-0.5 shrink-0"/> {h}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-pad bg-white">
        <div className="container-phs max-w-3xl">
          <form className="card-default" onSubmit={onSubmit} data-testid="insurance-form">
            <h2 className="font-display text-2xl text-[#071A33]">Request Insurance Guidance</h2>
            <p className="text-sm text-[#475569] mt-1">Tell us about the damage, the claim and what help you need. Upload any documents, photos or contractor reports.</p>

            <div className="grid sm:grid-cols-2 gap-5 mt-6">
              <Field label="Full name" required><input className="field-input" required value={form.full_name} onChange={e=>update("full_name", e.target.value)} data-testid="ins-full-name"/></Field>
              <Field label="Email" required><input className="field-input" type="email" required value={form.email} onChange={e=>update("email", e.target.value)} data-testid="ins-email"/></Field>
              <Field label="Phone" required><input className="field-input" required value={form.phone} onChange={e=>update("phone", e.target.value)} data-testid="ins-phone"/></Field>
              <Field label="Park name"><input className="field-input" value={form.park_name} onChange={e=>update("park_name", e.target.value)} data-testid="ins-park"/></Field>
              <Field label="Plot number"><input className="field-input" value={form.plot_number} onChange={e=>update("plot_number", e.target.value)} data-testid="ins-plot"/></Field>
              <Field label="Active claim?">
                <select className="field-input" value={form.active_claim ? "yes" : "no"} onChange={e=>update("active_claim", e.target.value === "yes")} data-testid="ins-active-claim">
                  <option value="no">No</option>
                  <option value="yes">Yes</option>
                </select>
              </Field>
              <Field label="Insurer name"><input className="field-input" value={form.insurer_name} onChange={e=>update("insurer_name", e.target.value)} data-testid="ins-insurer"/></Field>
              <Field label="Claim number"><input className="field-input" value={form.claim_number} onChange={e=>update("claim_number", e.target.value)} data-testid="ins-claim-number"/></Field>
              <Field label="Incident date"><input className="field-input" type="date" value={form.incident_date} onChange={e=>update("incident_date", e.target.value)} data-testid="ins-date"/></Field>
            </div>

            <div className="mt-6">
              <label className="field-label">Cause / damage summary <span className="text-[#B88A2A]">*</span></label>
              <textarea className="field-input" required value={form.cause_summary} onChange={e=>update("cause_summary", e.target.value)} data-testid="ins-cause"/>
            </div>

            <div className="mt-6">
              <label className="field-label">What help do you need? <span className="text-[#B88A2A]">*</span></label>
              <textarea className="field-input" required value={form.help_needed} onChange={e=>update("help_needed", e.target.value)} data-testid="ins-help"/>
            </div>

            <div className="mt-6">
              <FileUpload label="Documents, photos or videos" testId="ins-upload" onChange={setUploadIds}/>
            </div>

            <button disabled={submitting} className="btn-primary mt-7" data-testid="ins-submit-btn">
              {submitting ? <><Loader2 className="animate-spin" size={16}/> Sending…</> : <>Request Insurance Guidance <ArrowRight size={16}/></>}
            </button>
            <p className="mt-3 text-xs text-[#475569] flex items-start gap-1.5">
              <ShieldCheck size={14} className="text-[#0F766E] mt-0.5 shrink-0" />
              Non-regulated technical guidance only. Final insurance decisions remain with you and the relevant insurer.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="field-label">{label}{required ? <span className="text-[#B88A2A]"> *</span> : null}</label>
      {children}
    </div>
  );
}

function Confirmation() {
  return (
    <section className="bg-[#F7F4ED] section-pad">
      <div className="container-phs max-w-2xl">
        <div className="card-default text-center" data-testid="ins-confirmation">
          <h1 className="font-display text-3xl text-[#071A33]">Insurance enquiry received</h1>
          <p className="mt-3 text-[#334155]">We'll review the documents and information provided, then reply with non-regulated technical guidance.</p>
          <div className="mt-6 flex justify-center gap-3 flex-wrap">
            <Link to="/membership" className="btn-primary">Join Park Home Support</Link>
            <Link to="/" className="btn-outline">Back to Home</Link>
          </div>
        </div>
      </div>
    </section>
  );
}
